import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Empty, Space, Tabs, Typography } from 'antd'
import type { TabsProps } from 'antd'
import { Link } from 'react-router-dom'
import { PlusOutlined } from '@ant-design/icons'
import { ImportUsersBlock } from '../../components/v2/import-users-block'
import { ImportOrdersBlock } from '../../components/v2/import-orders-block'
import fetchAPI from '../../../lib/utils/fetch-api'
import type { CompanyStructure } from './company/types'
import {
  flattenOfficeOptionsForOrders,
  flattenOfficeOptionsForUsers,
  hasDealerOffices,
} from './import-utils'

const { Text } = Typography

type Props = {
  structure: CompanyStructure | null
  onUsersImported?: () => void
  /** Вкладка, открытая по умолчанию (если доступна) */
  defaultActiveTab?: 'users' | 'orders'
  /**
   * Какие импорты показывать (по умолчанию — оба).
   * Если остаётся один — выводится сразу, без вкладок и подсказки о порядке.
   */
  tabs?: Array<'users' | 'orders'>
}

export function HomeImportSection({ structure, onUsersImported, defaultActiveTab, tabs }: Props) {
  const [canImportOrders, setCanImportOrders] = useState(false)

  const userOfficeOptions = useMemo(
    () => (structure ? flattenOfficeOptionsForUsers(structure) : []),
    [structure],
  )

  const orderOfficeOptions = useMemo(
    () => (structure ? flattenOfficeOptionsForOrders(structure) : []),
    [structure],
  )

  const showUsersTab = !tabs || tabs.includes('users')
  const showOrdersTab =
    (!tabs || tabs.includes('orders')) && Boolean(structure && hasDealerOffices(structure))

  useEffect(() => {
    fetchAPI('/api/user_permissions')
      .then(perms =>
        setCanImportOrders(Array.isArray(perms) && perms.includes('admin_orders_import')),
      )
      .catch(() => setCanImportOrders(false))
  }, [])

  const firstOrgId = userOfficeOptions[0]?.organizationId

  const tabItems = useMemo((): NonNullable<TabsProps['items']> => {
    const items: NonNullable<TabsProps['items']> = []

    if (showUsersTab) {
      items.push({
        key: 'users',
        label: 'Сотрудники',
        children: (
          <ImportUsersBlock
            variant="inline"
            officeOptions={userOfficeOptions}
            onImported={onUsersImported}
          />
        ),
      })
    }

    if (showOrdersTab) {
      items.push({
        key: 'orders',
        label: 'Заказы',
        disabled: !canImportOrders,
        children: canImportOrders ? (
          <ImportOrdersBlock variant="inline" officeOptions={orderOfficeOptions} />
        ) : (
          <Text type="secondary">Нет права admin_orders_import</Text>
        ),
      })
    }

    return items
  }, [
    canImportOrders,
    onUsersImported,
    orderOfficeOptions,
    showOrdersTab,
    showUsersTab,
    userOfficeOptions,
  ])

  if (!structure) {
    return <Text type="secondary">Загрузка данных для импорта…</Text>
  }

  if (!userOfficeOptions.length) {
    return (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет офисов для импорта">
        {firstOrgId ? (
          <Link to={`/office/${firstOrgId}/new`}>
            <Button type="primary" icon={<PlusOutlined />}>
              Создать офис
            </Button>
          </Link>
        ) : (
          <Link to="/new/organizations">
            <Button type="primary">К организациям</Button>
          </Link>
        )}
      </Empty>
    )
  }

  const requestedSingle = Boolean(tabs && tabs.length === 1)
  if (requestedSingle) {
    if (!tabItems.length) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            tabs?.[0] === 'orders'
              ? 'Импорт заказов доступен для организаций типа «Дилер» или «Головной офис» — подходящих офисов пока нет'
              : 'Нет доступных импортов'
          }
        />
      )
    }
    return <>{tabItems[0].children}</>
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="Рекомендуемый порядок"
        description="1. Создайте офисы → 2. Импортируйте сотрудников → 3. Импортируйте заказы"
      />

      <Tabs
        key={canImportOrders ? 'orders-enabled' : 'orders-disabled'}
        items={tabItems}
        defaultActiveKey={
          defaultActiveTab &&
          tabItems?.some(item => item.key === defaultActiveTab && !item.disabled)
            ? defaultActiveTab
            : undefined
        }
      />
    </Space>
  )
}

export default HomeImportSection
